/**
 * Agent Approval-history feature — Zod schemas.
 *
 * The admin OVERSIGHT log of agent T3 approval decisions: who approved/denied which high-value
 * agent action. READ-ONLY — a T3 approval is passkey-bound to the delegating human (WYSIWYS), so
 * there is no decide action here.
 *
 * The response is the contract's ApprovalHistoryList, generated into api.generated.ts by make
 * docs; this module never restates it. `approver` is the typed principal, omitted when human_sub
 * did not resolve (the UI falls back to human_sub); `auth_req_id` keys the portal deep-link to a
 * still-pending approval; `grant_jti` keys the grant's signed evidence chain.
 */

import { z } from "zod";

import { approvalHistoryResponseSchema, approvalHistoryRowSchema } from "./api.generated";

export { approvalHistoryResponseSchema, approvalHistoryRowSchema };
export type ApprovalHistoryRow = z.infer<typeof approvalHistoryRowSchema>;
export type ApprovalHistoryResponse = z.infer<typeof approvalHistoryResponseSchema>;

export const approvalStatusSchema = approvalHistoryRowSchema.shape.status;
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

// URL is the source of truth for the filter state. `days` scopes the analytics summary
// panel above the log (7/30/90; anything else falls back to the default) — the log rows
// themselves are the complete record, unwindowed. It also keeps old Trends deep links
// (?days=…) meaningful after their route folded into this page.
export const approvalHistorySearchSchema = z.object({
  status: approvalStatusSchema.optional(),
  offset: z.number().int().min(0).optional().default(0),
  days: z.coerce
    .number()
    .int()
    .refine((n) => [7, 30, 90].includes(n))
    .optional(),
});
export type ApprovalHistorySearch = z.infer<typeof approvalHistorySearchSchema>;
